/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量添加照片列表组件 - 重写版
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-02-10 11:23:58
 */
import React, { useCallback, useRef, useMemo, useState, useEffect } from 'react'
import {
  AutoSizer,
  Grid,
  GridCellProps,
  GridOnScrollProps
} from 'react-virtualized'
import { Empty } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'
import { fetchMorePhotos } from '../../../../../actions/photo'
import actionTypes from '../../../../../constants/actionTypes'
import { buildFetchPhotosParamsWithOptions } from '@/pages/components/photoLib/photo'
import Photo from '../../../../components/Photo'
import backTop from '../../../../../images/back_top.png'
import { useTeamPhotoPickerCtx } from '../context'
import { useAddPhotosTrack } from '../hooks/useAddPhotosTrack'
import './PhotosList.scss'

// 常量定义
const COLUMN_WIDTH = 238
const ROW_HEIGHT = 280

interface PhotosListProps {
  inModal?: boolean
}

const PhotosList: React.FC<PhotosListProps> = ({ inModal = false }) => {
  const dispatch = useAppDispatch()
  const gridRef = useRef<Grid>(null)

  // Redux State
  const photos = useAppSelector((state) => state.photo.photos)
  const checkedPhotos = useAppSelector((state) => state.photo.checkedPhotos)
  const unCheckedPhotos = useAppSelector((state) => state.photo.unCheckedPhotos)
  const checkAll = useAppSelector((state) => state.photo.checkAll)
  const lastPageCond = useAppSelector((state) => state.photo.lastPageCond)
  const queryFilter = useAppSelector((state) => state.photo.queryFilter)
  const filter = useAppSelector((state) => state.photo.filter)
  const groupId = useAppSelector((state) => state.group.groupId)
  const contentIsAscSort = useAppSelector(
    (state) => state.photosByContent.isAscSort
  )
  // 已选照片来源:aiClerk 流走 Context.excludePhotos,batch-editor 流走 redux
  const { excludePhotos, relaxFilter } = useTeamPhotoPickerCtx()
  const photoPickerPhotos = useAppSelector((state) => {
    if (state.photoPicker.from === 'batch-editor') {
      return state.photoPicker.photos
    }
    return []
  })
  const selectedPhotosFromPicker = useMemo(
    () => (excludePhotos.length > 0 ? excludePhotos : photoPickerPhotos),
    [excludePhotos, photoPickerPhotos]
  )

  // 滚动状态 Ref
  const scrollFetchingRef = useRef(false)
  const autoFetchingRef = useRef(false)
  const lastFilteredCountBeforeLoadRef = useRef(0)
  const [showBackTop, setShowBackTop] = useState(false)
  const lastModalFetchRowCountRef = useRef(0)

  // 使用埋点 hook
  const { trackAddPhotos } = useAddPhotosTrack()

  const buildThumbUrl = useCallback((url?: string): string => {
    if (!url) return ''
    if (url.startsWith('data:') || url.startsWith('blob:')) {
      return url
    }
    const baseUrl = url.split('?')[0]
    return `${baseUrl}?x-oss-process=image/resize,w_200/quality,q_70`
  }, [])

  // 检查照片是否有防伪码
  const hasAntiFakeCode = useCallback((photo: any): boolean => {
    try {
      const extension = photo.extension
      if (!extension) {
        return false
      }
      // extension 可能是字符串或对象
      const extensionObj =
        typeof extension === 'string' ? JSON.parse(extension) : extension
      const antiFakeCode = extensionObj?.antiFakeCode
      return antiFakeCode && antiFakeCode.trim().length > 0
    } catch (error) {
      console.error('解析 extension 失败:', error)
      return false
    }
  }, [])

  // 过滤照片:
  //   relaxFilter=true (aiClerk 全量选图):后端返回什么就展示什么,客户端不再过滤
  //   relaxFilter=false (batch-editor 原行为):严格筛选 watermark / 防伪码 / baseID
  const filteredPhotos = useMemo(() => {
    if (!photos || photos.length === 0) {
      return photos
    }
    if (relaxFilter) return photos
    const selectedSmallURLs = new Set(
      selectedPhotosFromPicker.map((p: any) => p.smallURL)
    )
    return photos.filter((photo: any) => {
      const hasWatermark = photo.hasWatermark === true
      const isImage = photo.mediaType === 0
      const notSelected = !selectedSmallURLs.has(photo.smallURL)
      const photoWatermarkBaseID = photo.watermarkBaseID
      const isNotWatermarkBaseID21 = photoWatermarkBaseID !== '21'
      const hasAntiFake = hasAntiFakeCode(photo)
      return (
        hasWatermark &&
        isImage &&
        notSelected &&
        isNotWatermarkBaseID21 &&
        hasAntiFake
      )
    })
  }, [photos, selectedPhotosFromPicker, hasAntiFakeCode, relaxFilter])

  // 计算是否准备好选择（只要有选中项或全选状态，就显示 checkbox）
  const readyToCheck = useMemo(
    () =>
      !!Object.keys(checkedPhotos).length ||
      !!Object.keys(unCheckedPhotos).length ||
      checkAll,
    [checkedPhotos, unCheckedPhotos, checkAll]
  )

  // 选中照片
  const handlePushCheckedPhoto = useCallback(
    (photo: any) => {
      // 埋点：选择一张照片
      const searchKeywords = filter.searchKeywords
      trackAddPhotos({
        actionType: 'chooseOne',
        searchWord:
          searchKeywords && searchKeywords.length > 0
            ? searchKeywords[0] || ''
            : ''
      })

      if (checkAll) {
        dispatch({ type: actionTypes.REMOVE_UNCHECKED_PHOTO, data: photo })
      } else {
        dispatch({ type: actionTypes.PUSH_CHECKED_PHOTO, data: photo })
      }
    },
    [checkAll, dispatch, trackAddPhotos, filter]
  )

  // 取消选中照片
  const handleRemoveCheckedPhoto = useCallback(
    (photo: any) => {
      // 埋点：取消选择一张照片
      const searchKeywords = filter.searchKeywords
      trackAddPhotos({
        actionType: 'unChooseOne',
        searchWord:
          searchKeywords && searchKeywords.length > 0
            ? searchKeywords[0] || ''
            : ''
      })

      if (checkAll) {
        dispatch({ type: actionTypes.PUSH_UNCHECKED_PHOTO, data: photo })
      } else {
        dispatch({ type: actionTypes.REMOVE_CHECKED_PHOTO, data: photo })
      }
    },
    [checkAll, dispatch, trackAddPhotos, filter]
  )

  // 加载更多
  const handleFetchMore = useCallback(() => {
    if (!lastPageCond || scrollFetchingRef.current) return
    scrollFetchingRef.current = true

    dispatch(
      fetchMorePhotos(
        buildFetchPhotosParamsWithOptions({
          queryFilter: queryFilter,
          groupID: groupId,
          lastPageCond,
          isAscSort: contentIsAscSort
        })
      )
    )
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {})
      .finally(() => {
        scrollFetchingRef.current = false
      })
  }, [dispatch, groupId, lastPageCond, queryFilter, contentIsAscSort])

  // 当筛选后数据为空或数量较少时，自动加载更多数据
  useEffect(() => {
    const MIN_FILTERED_PHOTOS = 20

    if (
      lastFilteredCountBeforeLoadRef.current > 0 &&
      filteredPhotos.length === lastFilteredCountBeforeLoadRef.current &&
      filteredPhotos.length > 0
    ) {
      return
    }

    if (
      filteredPhotos.length < MIN_FILTERED_PHOTOS &&
      lastPageCond &&
      lastPageCond !== '' &&
      !autoFetchingRef.current &&
      !scrollFetchingRef.current &&
      photos.length > 0
    ) {
      lastFilteredCountBeforeLoadRef.current = filteredPhotos.length
      autoFetchingRef.current = true
      handleFetchMore()
      setTimeout(() => {
        autoFetchingRef.current = false
      }, 1000)
    } else if (filteredPhotos.length >= MIN_FILTERED_PHOTOS) {
      lastFilteredCountBeforeLoadRef.current = 0
    }
  }, [filteredPhotos.length, lastPageCond, photos.length, handleFetchMore])

  // 滚动处理
  const handleScroll = useCallback(
    ({ clientHeight, scrollHeight, scrollTop }: GridOnScrollProps) => {
      setShowBackTop(scrollTop > window.screen.height - 210)

      if (
        scrollHeight > clientHeight &&
        scrollHeight - clientHeight - scrollTop <= 0
      ) {
        handleFetchMore()
      }
    },
    [handleFetchMore]
  )

  const handleModalSectionRendered = useCallback(
    (params: any, rowCount: number) => {
      if (!inModal || rowCount <= 0) return
      if (params.rowStopIndex < rowCount - 1) return
      if (lastModalFetchRowCountRef.current === rowCount) return
      lastModalFetchRowCountRef.current = rowCount
      handleFetchMore()
    },
    [inModal, handleFetchMore]
  )

  // 回到顶部
  const handleBackTop = () => {
    if (gridRef.current) {
      gridRef.current.scrollToPosition({ scrollTop: 0, scrollLeft: 0 })
    }
  }

  // 单元格渲染
  const cellRenderer = ({
    columnIndex,
    key,
    rowIndex,
    style,
    parent
  }: GridCellProps & { parent: any }) => {
    const columnCount = parent.props.columnCount
    const photoIndex = rowIndex * columnCount + columnIndex

    if (photoIndex >= filteredPhotos.length) return null

    const photo = filteredPhotos[photoIndex]
    if (!photo) return null

    const isChecked =
      photo.seq in checkedPhotos ||
      (checkAll && !(photo.seq in unCheckedPhotos))

    const toggleCheck = () => {
      if (isChecked) {
        handleRemoveCheckedPhoto(photo)
      } else {
        handlePushCheckedPhoto(photo)
      }
    }

    const thumbUrl = buildThumbUrl(photo.smallURL)
    const renderPhoto = thumbUrl ? { ...photo, smallURL: thumbUrl } : photo

    return (
      <div key={key} style={style} className="add-batch-photos-list__cell">
        <div className="add-batch-photos-list__cell-inner">
          <Photo
            style={{ margin: 0 }}
            photo={renderPhoto}
            canCheck={true}
            readyToCheck={readyToCheck}
            checked={isChecked}
            onCheck={handlePushCheckedPhoto}
            onUnCheck={handleRemoveCheckedPhoto}
            onPreview={toggleCheck}
          />
        </div>
      </div>
    )
  }

  if (filteredPhotos.length === 0) {
    return (
      <div className="add-batch-photos-list add-batch-photos-list--empty">
        <Empty
          description="暂无照片"
          className="add-batch-photos-list__empty"
        />
      </div>
    )
  }

  return (
    <div className="add-batch-photos-list">
      <AutoSizer>
        {({ width, height }) => {
          const columnCount = Math.max(1, Math.floor(width / COLUMN_WIDTH))
          const rowCount = Math.ceil(filteredPhotos.length / columnCount)

          return (
            <Grid
              ref={gridRef}
              className="add-batch-photos-grid"
              width={width}
              height={height}
              columnWidth={COLUMN_WIDTH}
              columnCount={columnCount}
              rowHeight={ROW_HEIGHT}
              rowCount={rowCount}
              cellRenderer={cellRenderer}
              onScroll={handleScroll}
              onSectionRendered={
                inModal
                  ? (params) => handleModalSectionRendered(params, rowCount)
                  : undefined
              }
            />
          )
        }}
      </AutoSizer>

      <div
        className={`add-batch-photos-list__back-top${
          showBackTop ? ' is-visible' : ''
        }`}
      >
        <img
          className="add-batch-photos-list__back-top-icon"
          src={backTop}
          onClick={handleBackTop}
          alt="Back to top"
        />
      </div>
    </div>
  )
}

export default PhotosList
